import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, ShieldCheck } from "lucide-react";
import { fadeUp, stagger, EASE } from "@/lib/motion";

const SERVICES = [
  {
    id: "sourcing",
    title: "Category & Supplier Sourcing",
    desc: "We open vetted supplier networks across 18 European markets and beyond — benchmarking true landed cost and building dual-source resilience into every category.",
    deliverables: [
      "Vetted supplier shortlists with audited capacity",
      "Landed-cost benchmarking vs. incumbent terms",
      "Dual-source contingency maps per category",
    ],
    risk: "Every supplier passes financial, quality and ESG screening before introduction.",
    roi: "16% average landed-cost reduction",
  },
  {
    id: "private-label",
    title: "Private Label Development",
    desc: "Concept-to-shelf programmes that defend margin against discounters — tier architecture, packaging, compliance and supplier matching, delivered in sixteen weeks.",
    deliverables: [
      "Good-better-best tier architecture",
      "Packaging, labelling & EU compliance",
      "Factory matching and production QA",
    ],
    risk: "Pilot runs and shelf tests precede any full-range commitment.",
    roi: "+6–11 pts gross margin",
  },
  {
    id: "negotiations",
    title: "Trade Negotiations",
    desc: "We sit at the table with you. Joint business plans, rebate engineering and terms harmonisation — executed with a documented mandate and no supplier commissions.",
    deliverables: [
      "JBP structuring and annual terms strategy",
      "Rebate and retro engineering",
      "Listing-fee and payment-terms harmonisation",
    ],
    risk: "Independent by charter — we are paid by you, never by suppliers.",
    roi: "98.4% target-closure rate",
  },
  {
    id: "market-entry",
    title: "Market Entry Strategy",
    desc: "For brands and retailers crossing borders: market selection, distributor vetting and listing strategy that compresses time-to-shelf from years to months.",
    deliverables: [
      "Market attractiveness & entry sequencing",
      "Distributor and broker vetting",
      "Retail listing strategy & fee negotiation",
    ],
    risk: "Staged commitments with exit ramps at every gate.",
    roi: "9-month average time-to-shelf",
  },
  {
    id: "audits",
    title: "Merchandising & Store Audits",
    desc: "Shelf-level truth. Mystery audits, planogram compliance scoring and velocity analysis that reveal where margin leaks — and how to seal it.",
    deliverables: [
      "Planogram optimisation per store cluster",
      "Mystery audit programmes with scoring",
      "Compliance dashboards for category teams",
    ],
    risk: "Findings are benchmarked against 340+ network observations.",
    roi: "+12% shelf-level sales velocity",
  },
];

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="border-y border-line bg-sand/60 py-20 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={fadeUp} className="mb-4 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-forest">
              Strategic Services
            </span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="max-w-2xl font-serif text-2xl font-medium tracking-tight text-zinc-900 sm:text-3xl lg:text-4xl"
          >
            Five disciplines. One P&amp;L obsession.
          </motion.h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="flex flex-col gap-3 lg:col-span-5"
          >
            {SERVICES.map((s, i) => {
              const isActive = s.id === active;
              return (
                <motion.button
                  key={s.id}
                  variants={fadeUp}
                  data-testid={`service-tab-${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`group flex items-center justify-between border px-6 py-5 text-left transition-all duration-400 ${
                    isActive
                      ? "border-forest bg-white shadow-lg"
                      : "border-line bg-white/50 hover:border-gold hover:bg-white"
                  }`}
                >
                  <span className="flex items-center gap-5">
                    <span
                      className={`font-mono text-xs tracking-[0.2em] ${
                        isActive ? "text-gold" : "text-zinc-400"
                      }`}
                    >
                      0{i + 1}
                    </span>
                    <span
                      className={`font-serif text-lg font-semibold sm:text-xl ${
                        isActive ? "text-forest" : "text-zinc-800"
                      }`}
                    >
                      {s.title}
                    </span>
                  </span>
                  <ArrowUpRight
                    className={`h-5 w-5 shrink-0 transition-all duration-300 ${
                      isActive
                        ? "text-gold"
                        : "text-zinc-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold"
                    }`}
                  />
                </motion.button>
              );
            })}
          </motion.div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                data-testid="service-detail-panel"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="h-full border border-line bg-white p-8 shadow-sm lg:p-12"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  Advisory Domain
                </span>
                <h3 className="mt-3 font-serif text-2xl font-semibold text-zinc-900 sm:text-3xl">
                  {current.title}
                </h3>
                <p className="mt-5 text-base leading-relaxed text-zinc-600 sm:text-lg">
                  {current.desc}
                </p>

                <div className="mt-8 border-t border-line pt-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Key Deliverables
                  </span>
                  <ul className="mt-4 space-y-3">
                    {current.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-sm text-zinc-700 sm:text-base">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 flex items-start gap-3 border border-gold/40 bg-gold-soft px-5 py-4">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <p className="text-sm leading-relaxed text-zinc-700">{current.risk}</p>
                </div>

                <div className="mt-10 flex items-end justify-between">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                      ROI Benchmark
                    </span>
                    <p
                      data-testid="service-roi-benchmark"
                      className="mt-1 font-serif text-2xl font-semibold text-forest sm:text-3xl"
                    >
                      {current.roi}
                    </p>
                  </div>
                  <span className="font-serif text-6xl font-semibold text-sand" aria-hidden>
                    0{SERVICES.indexOf(current) + 1}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
